import React, { Fragment, useMemo } from "react";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

import { Link, useParams } from "react-router-dom";

/*
 * check permissions and figure if its:
 * not editable right now then: show info box saying why if its cuz of already under edit by author or mod
 * if not editable because of any other reason, simply show published page
 * else
 * is an author edit or mod edit
 * if author edit, do the get for edit bit
 * if mod edit, do the get for mod bit
 *
 * //parallel get the
 */

import { useAppSettingsContext } from "@/contexts/appSettingsContext";
import { useGenErrorMsgBanners } from "@/utils/useGenErrorMsgBanners";
import axios from "axios";
import { useQuery } from "react-query";
import { checkPermissionsToModifyDoc } from "../utils/checkPermissionsToModifyDoc";
import {
  colors2,
  UI_BODY_SEMIBOLD_DF,
  UI_TITLE_SM,
  icons,
  UI_HEADLINE_SM,
  SkeletonLoader,
  PaddingBottom10,
  PaddingBottom5,
  UI_BODY_SEMIBOLD_SM_DF,
  PaddingTopBottom5,
  PaddingTopBottom10,
} from "oolib";
import styled from "styled-components";
import ErrorStates from "@/components/ErrorStates";
import { EditBlockGenerator } from "@/utils/blockGenerators";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { contentBlockOptions } from "@/pageTpls/TCI/TCISections/TemplateBuilder/comps/panels/InputBlockConfigPanelTCI/config/contentBlockOptions";

const { CaretLeft } = icons;

// For view mode - cacheable (uses your global defaults)
const useGetViewDoc = ({ contentType, contentId, enabled = true }) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS.lang?.value;
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  return useQuery({
    queryKey: [
      "SM_ContentLanding",
      contentType,
      contentId,
      "view",
      { activeLang },
    ],
    queryFn: async () => {
      try {
        const viewUrl = `/api/content/viewContent/${contentType}/${contentId}`;
        const viewRes = await axios.get(viewUrl, { params: { activeLang } });
        return viewRes.data;
      } catch (error) {
        genErrorMsgBanners({ err: error });
        throw error;
      }
    },
    enabled,
  });
};

// For edit mode - never cached, always fresh
const useGetEditDoc = ({ contentType, contentId, enabled = false }) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  
  return useQuery({
    queryKey: ["SM_ContentLanding", contentType, contentId, "edit"],
    queryFn: async () => {
      try {
        const editUrl = `/api/content/getContentForEdit/${contentType}/${contentId}`;
        const editRes = await axios.patch(editUrl);
        return editRes.data;
      } catch (editError) {
        console.log("Error in useGetEditModOrViewDocAsPerAccess > getForEdit");
        genErrorMsgBanners({ err: editError });
        throw editError;
      }
    },
    enabled,
    cacheTime: 0,
    staleTime: 0,
  });
};

// For moderate mode - never cached, always fresh
const useGetModerateDoc = ({ contentType, contentId, enabled = false }) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  return useQuery({
    queryKey: ["SM_ContentLanding", contentType, contentId, "moderate"],
    queryFn: async () => {
      try {
        // Replace this URL with the actual moderation API endpoint
        // This is a placeholder - you'll need to provide the correct endpoint
        const moderateUrl = `/api/moderation/edit/${contentType}?id=${contentId}`;

        const moderateRes = await axios.get(moderateUrl);
        return moderateRes.data;
      } catch (moderateError) {
        console.log("Error in useGetEditModOrViewDocAsPerAccess > getForMod");
        genErrorMsgBanners({ err: moderateError });
        throw moderateError;
      }
    },
    enabled,
    cacheTime: 0,
    staleTime: 0,
  });
};

// Main orchestrating hook - preserves all your business logic
const useGetEditModOrViewDocAsPerAccess = ({
  contentType,
  contentId,
  user,
  enabled = true,
}) => {
  // Step 1: Always fetch view content first to get document data for permissions check
  const {
    data: viewData,
    status: viewStatus,
    error: viewError,
  } = useGetViewDoc({
    contentType,
    contentId,
    enabled,
  });

  // Step 2: Check permissions using the document data
  const _permissions = viewData
    ? checkPermissionsToModifyDoc(viewData.contribution, user)
    : null;

  // ---- TEMP FOR TESTING ---- //
  const permissions = {
    ..._permissions,
    canEdit: false,
    canModerate: false,
  };
  // ---- DELETE AFTER TESTING ----- //

  // Fetch edit data if user can edit
  const {
    data: editData,
    status: editStatus,
    error: editError,
  } = useGetEditDoc({
    contentType,
    contentId,
    enabled: permissions?.canEdit,
  });

  // Fetch moderate data if user can moderate (but not edit)
  const {
    data: moderateData,
    status: moderateStatus,
    error: moderateError,
  } = useGetModerateDoc({
    contentType,
    contentId,
    enabled: permissions?.canModerate,
  });

  // Return the appropriate data based on what was successfully fetched
  // Priority: Edit > Moderate > View (same as your original logic)
  if (permissions?.canEdit && editData) {
    return {
      data: {
        content: editData.contribution,
        tpl: editData.tpl,
        permissions,
        accessMode: "edit",
        info: {
          ...permissions.info,
          accessMode: "edit",
          userId: user?._id,
          contentId,
          contentType,
        },
      },
      status: editStatus,
      error: editError,
    };
  } else if (permissions?.canModerate && moderateData) {
    return {
      data: {
        content: moderateData.contribution,
        tpl: moderateData.tpl,
        permissions,
        accessMode: "moderate",
        info: {
          ...permissions.info,
          accessMode: "moderate",
          userId: user?._id,
          contentId,
          contentType,
        },
      },
      status: moderateStatus,
      error: moderateError,
    };
  } else if (viewData) {
    return {
      data: {
        content: viewData.contribution,
        tpl: viewData.tpl,
        permissions,
        accessMode: "view",
        info: {
          ...permissions.info,
          accessMode: "view",
          userId: user?._id,
          contentId,
          contentType,
        },
      },
      status: viewStatus,
      error: viewError,
    };
  }

  // Still loading or error state
  return {
    data: null,
    status: viewStatus,
    error: viewError,
  };
};

const StyledGridShell = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 700px) 260px;

  margin-top: 20px;
`;

const StyledBodyWrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 500px; // This sets your preferred maximum width
`;

const StyledBody = styled.div`
  width: 100%; // This makes it fill the available space
  max-width: 500px; // This prevents it from getting larger than 500px
`;

const StyledContextTagsContainer = styled.div`
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
  width: 260px;
`;

const StyledContentTagsHeader = styled.div`
  border-bottom: 1px solid ${colors2.grey10};
  padding: 10px 14px;
`;

const StyledContentTagsBody = styled.div`
  padding: 9px 14px;
`;

const ContentLandingSkeletonLoader = () => {
  return <div></div>;
};

export default ({}) => {
  const userData = useGetQueryData("userData");
  const params = useParams();

  //   const { data: tpl, status: fetchTplStatus } =
  //     useGetOnePublishedOrUnpublishedTpl({
  //       contentType: params.contentType,
  //     });
  // const

  const { data, status, error } = useGetEditModOrViewDocAsPerAccess({
    contentType: params.contentType,
    contentId: params.contentId,
    user: userData?.user,
  });

  // console.log({ data, status });

  if (status === "error") {
    return <ErrorStates errorResponseObject={error} />;
  }

  const { quantFieldOptions, qualFieldOptions } = useMemo(() => {
    const flattened = contentBlockOptions.reduce(
      (acc, next) => [...acc, ...(next?.variants || [next])],
      []
    );
    return {
      quantFieldOptions: flattened.filter((d) => d.qual_quant === "quant"),
      qualFieldOptions: flattened.filter((d) => d.qual_quant === "qual"),
    };
  }, []);

  const { contextTagBlocks, bodyBlocks } = useMemo(() => {
    if (status === "success") {
      const blocks = extractAllBlocksFromTpl({ tpl: data.tpl }).filter(
        (bl) => !["TitleInput", "SubtitleInput"].includes(bl.comp)
      );
      return {
        contextTagBlocks: blocks.filter((bl) =>
          quantFieldOptions.some((op) => op.comp === bl.comp)
        ),
        bodyBlocks: blocks.filter((bl) =>
          qualFieldOptions.some((op) => op.comp === bl.comp)
        ),
      };
    } else {
      return {
        contextTagBlocks: [],
        bodyBlocks: [],
      };
    }
  }, [data, status]);

  const generateBlocks = ({
    content,
    formValidation,
    BlockWrapper = PaddingTopBottom5,
    blocks,
    handleOnChange,
  }) => {
    return (
      <div>
        {blocks.map((block) => (
          <EditBlockGenerator
            key={block.valuePath}
            block={block}
            onChange={handleOnChange}
            content={content}
            Wrapper={BlockWrapper}
            formValidation={formValidation}
          />
        ))}
      </div>
    );
  };
  

  return (
    <StyledGridShell>
      <div style={{ padding: "0 16px" }}>
        <StyledBodyWrapper>
          <StyledBody>
            {status === "loading" ? (
              <SkeletonLoader style={{ height: "16px", width: "400px" }} />
            ) : (
              <Link
                to={`/datasets/${data.tpl?.kp_content_type}`}
                style={{
                  display: "flex",
                  gap: "2px",
                  alignItems: "center",
                  cursor: "pointer",
                  border: "none",
                  background: "unset",
                }}
              >
                <CaretLeft weight="bold" size={16} />
                <UI_BODY_SEMIBOLD_DF>
                  {`Datasets / ${
                    data.tpl?.general?.content?.title ||
                    data.tpl.kp_content_type
                  }`}
                </UI_BODY_SEMIBOLD_DF>
              </Link>
            )}

            <div style={{ marginTop: "4px" }}>
              {status === "loading" ? (
                <SkeletonLoader style={{ height: "36px", width: "350px" }} />
              ) : (
                <UI_HEADLINE_SM>{data.content?.main?.title}</UI_HEADLINE_SM>
              )}
            </div>
            <div style={{ marginTop: "18px" }}>
              {status === "loading" ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  {[1, 2, 3].map((d) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <SkeletonLoader style={{ height: "18px" }} />
                      <SkeletonLoader style={{ height: "14px" }} />
                      <SkeletonLoader style={{ height: "150px" }} />
                    </div>
                  ))}
                </div>
              ) : (
                generateBlocks({
                  content: data.content,
                  blocks: bodyBlocks,
                  BlockWrapper: PaddingTopBottom10,
                })
              )}
            </div>
          </StyledBody>
        </StyledBodyWrapper>
      </div>
      <StyledContextTagsContainer>
        <StyledContentTagsHeader>
          {status === "loading" ? (
            <SkeletonLoader style={{ height: "16px", width: "200px" }} />
          ) : (
            <UI_BODY_SEMIBOLD_SM_DF>Context Tags</UI_BODY_SEMIBOLD_SM_DF>
          )}
        </StyledContentTagsHeader>
        <StyledContentTagsBody>
          {status === "loading"
            ? [1, 2, 3].map((d) => (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <SkeletonLoader style={{ height: "14px" }} />
                  <SkeletonLoader style={{ height: "100px" }} />
                </div>
              ))
            : generateBlocks({
                content: data.content,
                blocks: contextTagBlocks,
              })}
        </StyledContentTagsBody>
      </StyledContextTagsContainer>
    </StyledGridShell>
  );
};
